import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { get } from "../api";
import { formatClock } from "../format";
import ReportView from "../report/ReportView";
import { useCustomer } from "./CustomerLayout";
import type { ReportDetail, ReportSummary } from "../api";

interface ReportList {
  customer: string;
  reports: ReportSummary[];
}

/**
 * One customer's report, for whichever run is picked.
 *
 * The report is rendered by the app itself, from the stored jsonb — see
 * `report/ReportView`. "Open full report" is the other rendering: the
 * standalone document `sage_report::render` produces, which is what the CLI
 * writes and what you would print or send on.
 */
export default function CustomerReport() {
  const { customer, refresh } = useCustomer();
  const ref = customer.name;
  const [params, setParams] = useSearchParams();
  const chosen = params.get("r");

  const [list, setList] = useState<ReportList | null>(null);
  const [detail, setDetail] = useState<ReportDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setList(null);
    setError(null);
    void get<ReportList>(`/api/customers/${ref}/reports`)
      .then(setList)
      .catch((e) => setError(e.message));
  }, [ref]);

  const reports = list?.reports ?? [];
  const current = chosen ? reports.find((r) => r.id === chosen) : reports[0];
  const currentId = current?.id;

  useEffect(() => {
    if (!currentId) return;
    setDetail(null);
    setError(null);
    void get<ReportDetail>(`/api/customers/${ref}/reports/${currentId}`)
      .then(setDetail)
      .catch((e) => setError(e.message));
  }, [ref, currentId]);

  if (error) return <div className="error">{error}</div>;
  if (!list) return <div className="empty">Loading…</div>;

  if (reports.length === 0) {
    return (
      <div className="empty">
        No report has been written for this customer yet.
        <br />
        <code>make report C={list.customer}</code>
      </div>
    );
  }

  return (
    <>
      <div className="picker">
        <label htmlFor="day" style={{ marginBottom: 0 }}>
          Report
        </label>
        <select
          id="day"
          value={current?.id ?? ""}
          onChange={(e) => setParams({ r: e.target.value })}
        >
          {reports.map((r, i) => (
            <option key={r.id} value={r.id}>
              {r.report_date}
              {i === 0 ? " (latest)" : ""} — {r.window_days}d {r.mode}
              {/* Append-only: one date can hold several runs, and which
                  is which only shows in the run timestamp. */}
              {reports.filter((o) => o.report_date === r.report_date).length > 1
                ? ` — run ${formatClock(r.created_at)}`
                : ""}
            </option>
          ))}
        </select>
        {currentId && (
          <a
            className="btn"
            href={`/api/customers/${ref}/reports/${currentId}/html`}
            target="_blank"
            rel="noreferrer"
          >
            Open full report ↗
          </a>
        )}
      </div>

      {!detail ? (
        <div className="empty">Loading…</div>
      ) : (
        <ReportView
          detail={detail}
          customer={ref}
          lifetimeRuns={customer.report_runs}
          lifetimeSpend={customer.spend_usd}
          onTracked={refresh}
        />
      )}
    </>
  );
}
